import React, { useContext, forwardRef, useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation } from 'react-router-dom';
import classnames from 'classnames';
import { useWindowSize } from 'react-use';
import { NavHashLink } from 'react-router-hash-link';
import { Manager, Popper } from 'react-popper';
import useEventOutside from '@omtanke/react-use-event-outside';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/icon/Icon';
import ThemeContext from '../../contexts/themeContext';
import Collapse from '../../components/bootstrap/Collapse';
import useDarkMode from '../../hooks/useDarkMode';
import './nav-light-mode.css';
import Tooltips from '../../components/bootstrap/Tooltips';

export const List = forwardRef(
	({ id, children, className, ariaLabelledby, parentId, rootId, horizontal, ...props }, ref) => {
		return (
			<ul
				ref={ref}
				id={id}
				className={classnames('navigation', { 'navigation-menu': horizontal }, className)}
				aria-labelledby={ariaLabelledby}
				data-bs-parent={
					parentId === `${rootId}__${rootId}`
						? `#${rootId}`
						: (parentId && `#${parentId}`) || null
				}
				{...props}>
				{children}
			</ul>
		);
	},
);
List.propTypes = {
	id: PropTypes.string,
	children: PropTypes.node,
	className: PropTypes.string,
	ariaLabelledby: PropTypes.string,
	parentId: PropTypes.string,
	rootId: PropTypes.string,
	horizontal: PropTypes.bool,
};
List.defaultProps = {
	id: null,
	children: null,
	className: null,
	ariaLabelledby: null,
	parentId: null,
	rootId: null,
	horizontal: false,
};

export const Item = ({
	children,
	to,
	title,
	icon,
	id,
	parentId,
	rootId,
	isHorizontal,
	notification,
	isMore,
	hide,
	asideStatus,

	...props
}) => {
	const { darkModeStatus } = useDarkMode();
	const { width } = useWindowSize();
	const { setAsideStatus, setLeftMenuStatus, setRightMenuStatus } = useContext(ThemeContext);

	const _active = Array.isArray(props.expandedIds) && props.expandedIds.includes(id);

	const handleClick = () => {
		props.setExpandedIds((prev) => {
			const list = Array.isArray(prev) ? prev : [];
			return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
		});
	};

	const linkHandleClick = () => {
		if (parentId && parentId !== rootId) {
			props.setExpandedIds([parentId]);
		} else {
			props.setExpandedIds([]);
		}
		if (width < process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE) {
			setAsideStatus(!asideStatus);
		}
	};

	const _anchorLinkPattern = /^#/i;
	const location = useLocation();

	const here = to !== '/' && (location.pathname === to || location.pathname.startsWith(to + '/'));

	const match = to !== '/' && location.pathname === to;

	const { t } = useTranslation();

	const isParentItem = !!children;
	const _LinkClass = classnames(
		'navigation-link',
		'navigation-link-pill',
		{
			collapsed: isParentItem && !isHorizontal,
			active: !isParentItem && (isHorizontal ? match : here),
		},
		isParentItem && 'navigation-link-parent',
		_active && isParentItem && 'expanded',
	);

	const _InnerContent = (
		<>
			{icon && (
				<Icon
					className='navigation-icon'
					style={{ color: darkModeStatus ? undefined : '#323232' }}
					icon={icon}
				/>
			)}
			<span className='navigation-text'>{t(title)}</span>
		</>
	);

	const _Inner = (
		<>
			<span
				className='navigation-link-info'
				style={{ color: darkModeStatus ? undefined : '#323232', position: 'relative' }}>
				{_InnerContent}
				{!asideStatus && (
					<Tooltips title={t(title)} placement='right' isDisplayInline>
						<span style={{ position: 'absolute', inset: 0 }} />
					</Tooltips>
				)}
			</span>
			{(!!children || !!notification) && (
				<span
					className='navigation-link-extra'
					style={{ color: darkModeStatus ? undefined : '#323232' }}>
					{!!notification && (
						<Icon
							icon='Circle'
							className={classnames(
								'navigation-notification',
								{
									[`text-${notification}`]: typeof notification === 'string',
									'text-danger': typeof notification !== 'string',
								},
								'animate__animated animate__heartBeat animate__infinite animate__slower',
							)}
						/>
					)}
					{!!children && <Icon className='navigation-arrow' icon='ChevronRight' />}
				</span>
			)}
		</>
	);

	const _withoutChild =
		!children &&
		!hide &&
		((_anchorLinkPattern.test(to) && (
			<NavHashLink
				className={_LinkClass}
				to={to}
				onClick={linkHandleClick}
				title={!props.asideStatus ? t(title) : undefined}>
				{_Inner}
			</NavHashLink>
		)) || (
			<NavLink
				className={classnames(_LinkClass, ({ isActive }) => (isActive ? 'active' : ''))}
				to={`../${to}`}
				onClick={linkHandleClick}
				title={!props.asideStatus ? t(title) : undefined}>
				{_Inner}
			</NavLink>
		));

	const dropdownRef = useRef(null);

	const dropdownListRef = useRef(null);
	const setListRef = useCallback((node, ref) => {
		dropdownListRef.current = node;
		return ref(node);
	}, []);

	const [dropdownStatus, setDropdownStatus] = useState(false);

	const closeMenu = useCallback(() => {
		setDropdownStatus(false);
	}, []);
	useEventOutside(dropdownRef, 'mousedown', closeMenu);
	useEventOutside(dropdownRef, 'touchstart', closeMenu);

	if (hide) {
		return null;
	}

	if (children) {
		if (isHorizontal) {
			return (
				<Manager>
					<li
						ref={dropdownRef}
						className={classnames('navigation-item', 'dropdown', {
							'navigation-item-more': isMore,
						})}>
						{dropdownStatus && (
							<Popper
								placement='bottom-start'
								modifiers={[
									{
										name: 'flip',
										options: {
											fallbackPlacements: [`bottom-end`, `bottom-start`],
										},
									},
								]}>
								{({ ref, style, placement }) => (
									<List
										ref={(node) => setListRef(node, ref)}
										style={style}
										data-placement={placement}
										id={`${rootId}__${id}`}
										className={classnames(
											'dropdown-menu',
											{
												'dropdown-menu-dark': darkModeStatus,
											},
											'show',
										)}
										ariaLabelledby={`${rootId}__${id}--link`}
										rootId={rootId}
										parentId={`${rootId}__${parentId}`}
										onMouseLeave={() => setDropdownStatus(false)}>
										{children}
									</List>
								)}
							</Popper>
						)}
					</li>
				</Manager>
			);
		}

		return (
			<li className='navigation-item'>
				<span
					id={`${rootId}__${id}--link`}
					className={_LinkClass}
					aria-expanded={_active}
					aria-controls={`${rootId}__${id}`}
					role='button'
					tabIndex='-1'
					onClick={handleClick}
					onKeyDown={handleClick}>
					{_Inner}
				</span>
				<Collapse isOpen={_active} isChildClone>
					<List
						id={`${rootId}__${id}`}
						ariaLabelledby={`${rootId}__${id}--link`}
						rootId={rootId}
						parentId={`${rootId}__${parentId}`}
						onMouseLeave={closeMenu}>
						{children}
					</List>
				</Collapse>
			</li>
		);
	}

	return <li className='navigation-item'>{_withoutChild}</li>;
};

Item.propTypes = {
	children: PropTypes.node,
	to: PropTypes.string,
	title: PropTypes.string,
	icon: PropTypes.string,
	id: PropTypes.string,
	parentId: PropTypes.string,
	rootId: PropTypes.string.isRequired,
	isHorizontal: PropTypes.bool,
	notification: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
	isMore: PropTypes.bool,
	hide: PropTypes.bool,
	asideStatus: PropTypes.bool,
	setAsideStatus: PropTypes.func,
	expandedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
	setExpandedIds: PropTypes.func,
};

Item.defaultProps = {
	children: null,
	to: null,
	title: null,
	icon: null,
	id: null,
	parentId: null,
	isHorizontal: false,
	notification: false,
	isMore: false,
	hide: false,
	asideStatus: false,
	setAsideStatus: () => {},
	expandedIds: [],
	setExpandedIds: () => {},
};

export const NavigationLine = ({ className }) => {
	const { darkModeStatus } = useDarkMode();

	return (
		<hr
			style={{ borderBottomColor: darkModeStatus ? undefined : '#323232' }}
			className={classnames('navigation-line', className)}
		/>
	);
};
NavigationLine.propTypes = {
	className: PropTypes.string,
};
NavigationLine.defaultProps = {
	className: null,
};

export const NavigationTitle = ({ className, children, ...props }) => {
	const { darkModeStatus } = useDarkMode();

	return (
		<li className='navigation-item'>
			<span
				style={{ color: darkModeStatus ? undefined : '#323232' }}
				className={classnames('navigation-title', className)}
				{...props}>
				{children}
			</span>
		</li>
	);
};
NavigationTitle.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
NavigationTitle.defaultProps = {
	className: null,
};

const Navigation = forwardRef(
	({ menu, horizontal, id, className, asideStatus, setAsideStatus, ...props }, ref) => {
		const [expandedIds, setExpandedIds] = useState([]);

		const { t } = useTranslation();

		const fallbackIcons = {
			// Appointments submenu
			v_up_book: 'Schedule',
			v_completed_book: 'TaskAlt',
			v_expired_book: 'HistoryToggleOff',
			v_cancelled_book: 'Cancel',
			v_assign_book: 'Assignment',
			v_cartp: 'ShoppingCart',
			// Services submenu
			add_serv: 'Dns',
			add_cat: 'Category',
			add_promo: 'LocalOffer',
			view_serv: 'Visibility',
			// Employees submenu
			emp_list: 'Group',
			emp_shed: 'Event',
			emp_work_shed: 'CalendarMonth',
			emp_leave: 'Logout',
			// Transaction submenu
			manage_pay: 'ReceiptLong',
			income: 'TrendingUp',
			expences: 'TrendingDown',
			cheque: 'RequestQuote',
			extype: 'Category',
			// Reports submenu
			orders: 'ShoppingBag',
			appointment: 'EventNote',
			product: 'Inventory2',
			inventory: 'Inventory',
			manage_comm: 'Paid',
			payment: 'CreditCard',
			shedule: 'Today',
			attendance: 'HowToReg',
			prefund: 'Pending',
			crefund: 'DoneAll',
			// Refunds submenu
			refund_list: 'Replay',
			refund_havetopay: 'Payments',
			refund_paid: 'Done',
		};

		function fillMenu(data, parentId, rootId, isHorizontal, isMore) {
			return Object.keys(data).map((item) =>
				data[item].path ? (
					<Item
						key={data[item].id}
						rootId={rootId}
						id={data[item].id}
						title={data[item].text}
						icon={
							data[item].icon ||
							(parentId !== rootId
								? fallbackIcons[data[item].id] || 'Circle'
								: undefined)
						}
						to={`${data[item].path}`}
						parentId={parentId}
						isHorizontal={isHorizontal}
						expandedIds={expandedIds}
						setExpandedIds={setExpandedIds}
						notification={data[item].notification}
						hide={data[item].hide}
						asideStatus={asideStatus}
						setAsideStatus={setAsideStatus}>
						{!!data[item].subMenu &&
							fillMenu(data[item].subMenu, data[item].id, rootId, isHorizontal)}
					</Item>
				) : (
					!isMore &&
					!isHorizontal && (
						/* i18next-extract-disable-next-line */
						<NavigationTitle key={data[item].id}>{t(data[item].text)}</NavigationTitle>
					)
				),
			);
		}

		return (
			<nav ref={ref} aria-label={id} className={className} {...props}>
				<List id={id} horizontal={horizontal}>
					{fillMenu(menu, id, id, horizontal)}
					{horizontal && (
						<Item
							rootId={`other-${id}`}
							title={t('More')}
							icon='MoreHoriz'
							isHorizontal
							isMore>
							{fillMenu(menu, `other-${id}`, `other-${id}`, false, true)}
						</Item>
					)}
				</List>
			</nav>
		);
	},
);
Navigation.propTypes = {
	horizontal: PropTypes.bool,
	menu: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		text: PropTypes.string,
		path: PropTypes.string,
		icon: PropTypes.string,
		isDisable: PropTypes.bool,
		subMenu: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
				text: PropTypes.string,
				path: PropTypes.string,
				icon: PropTypes.string,
				isDisable: PropTypes.bool,
			}),
		),
	}).isRequired,
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	className: PropTypes.string,
	asideStatus: PropTypes.bool,
	setAsideStatus: PropTypes.func,
};
Navigation.defaultProps = {
	horizontal: false,
	className: null,
	asideStatus: false,
	setAsideStatus: () => {},
};

export default Navigation;
